import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Loader2 } from "lucide-react";

const FIELDS = [
  { group: "Margin & Markup", items: [
    ["default_markup_pct", "Default Markup %"],
    ["min_margin_pct", "Minimum Margin %"],
    ["min_freight_charge", "Minimum Freight Charge ($)"],
    ["handling_fee", "Handling / Admin Fee ($)"],
    ["insurance_pct", "Insurance % of declared value"],
  ]},
  { group: "Accessorial Defaults ($)", items: [
    ["liftgate_fee", "Liftgate"],
    ["residential_adjustment", "Residential"],
    ["inside_delivery_fee", "Inside Delivery"],
    ["limited_access_fee", "Limited Access"],
    ["appointment_fee", "Appointment"],
    ["notify_fee", "Notify Before Delivery"],
    ["over_dimensional_fee", "Over-Dimensional"],
    ["hazmat_fee", "Hazmat"],
  ]},
];

export default function Rules() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/rules").then((r) => setRules(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.flatMap((g) => g.items).forEach(([k]) => (payload[k] = Number(rules[k])));
      await api.put("/rules", payload);
      toast.success("Pricing rules saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!rules) return <div className="text-muted-foreground">Loading rules…</div>;

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Pricing & Rules Engine</h1>
          <p className="mt-1 text-sm text-muted-foreground">Markup, margin floors, handling and accessorial defaults applied to every quote</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-rules-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Save Rules</>}
        </Button>
      </div>

      {FIELDS.map((g) => (
        <div key={g.group} className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-caps">{g.group}</div>
          <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-4">
            {g.items.map(([k, label]) => (
              <div key={k}>
                <Label className="label-caps">{label}</Label>
                <Input type="number" value={rules[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5 h-10" data-testid={`rule-${k}`} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="text-xs text-muted-foreground">
        Future-proofing: product-type markup, customer-type margin, lane/zone rules and carrier-preference rules plug into this same engine.
      </p>
    </div>
  );
}
